import { memo, useCallback, useState } from 'react';

import { cn } from '@/shadcn/lib/utils';

import { TestId } from '@/types/testId';
import { useFocus } from '@/utils/hooks';
import { ValueLabel } from './valueLabel/valueLabel';

type Props = {
  values?: string[];
  updateValues: (values: string[]) => void;
  placeholder?: string;
  splitKeys?: string[];
  toLowerCase?: boolean;
  errorFn?: (value: string) => string | null;
};

/**
 * This component behaves like a textarea
 * where the user can input multiple independent string values
 * that can be confirmed by hitting Enter, and also closed one by one
 */
export const MultipleValuesTextArea = memo(
  ({
    values = [],
    updateValues,
    placeholder = 'Type a new value',
    splitKeys = ['Enter', 'Tab'],
    toLowerCase = false,
    errorFn = () => null
  }: Props) => {
    // state for focusing text input
    const [isFocused, focus, blur, inputRef] = useFocus(false);

    // callbacks for updating values list
    const addValue = useCallback(
      value =>
        updateValues([...values, toLowerCase ? value.toLowerCase() : value]),
      [values, updateValues, toLowerCase]
    );
    const removeValue = useCallback(
      valueIdx => {
        const newValues = [...values];
        newValues.splice(valueIdx, 1);
        updateValues(newValues);

        // clicking the close icon has blurred the input hence changed the border color
        // so focus it explicitly again
        if (inputRef.current) inputRef.current.focus();
      },
      [values, updateValues]
    );

    // state for inputting a new value
    const [inputValue, setInputValue] = useState('');

    // state for error handling
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // callbacks to update input
    const handleChange = useCallback(
      event => {
        setInputValue(event.target.value);
        // clean error as soon as new text typed in
        setErrorMsg(null);
      },
      [inputValue, addValue]
    );

    // callbacks to listen for key events
    // check if the event key is in splitKeys
    const handleKeyDown = useCallback(
      event => {
        if (inputValue.length && splitKeys.includes(event.key)) {
          event.preventDefault();
          const validationErrMessage = errorFn(inputValue);
          if (validationErrMessage) {
            setErrorMsg(validationErrMessage);
            return;
          }
          addValue(inputValue.trim());
          setInputValue('');
        }
      },
      [inputValue, addValue, splitKeys, errorFn]
    );

    return (
      <div className="flex flex-col flex-nowrap items-stretch overflow-hidden">
        <div
          className={cn(
            'flex min-h-[80px] flex-row flex-wrap items-start rounded-md border border-input bg-background p-1 transition-colors',
            isFocused && 'border-primary',
            !!errorMsg && 'border-destructive'
          )}
        >
          {values.map((value, index) => (
            <ValueLabel
              index={index} // note: might have same value string accross the list so to ensure unique keys we mix index and value
              key={`${value}-${index}`}
              removeValue={removeValue}
              value={value}
            />
          ))}
          <input
            autoFocus
            className="m-1 mt-2 box-border inline-block min-w-[200px] grow appearance-none rounded-md border-0 bg-transparent px-1.5 py-0.5 text-foreground text-sm outline-none placeholder:truncate placeholder:text-muted-foreground"
            data-testid={TestId.MultipleValuesTextArea.TextInput}
            onBlur={blur}
            onChange={handleChange}
            onFocus={focus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            ref={inputRef}
            tabIndex={0}
            type="text"
            value={inputValue}
          />
        </div>
        {errorMsg && (
          <div className="mt-1.5 truncate text-destructive text-xs">
            {errorMsg}
          </div>
        )}
      </div>
    );
  }
);
